import { useState, useEffect, useCallback } from "react";
import { V } from "../theme.js";
import { OptBtn, AdjRow, Modal } from "../ui/components.jsx";

const FORMATS = [
  { id:"png",  label:"PNG",  mime:"image/png",  hasQuality:false },
  { id:"jpg",  label:"JPEG", mime:"image/jpeg", hasQuality:true  },
  { id:"webp", label:"WebP", mime:"image/webp", hasQuality:true  },
];

/**
 * ExportDialog — export with format, quality, scale, filename.
 *
 * Props:
 *   open        boolean
 *   canvasW     number — current canvas width
 *   canvasH     number — current canvas height
 *   onExport    (options) => void — { format, mime, quality, scale, filename }
 *   onClose     () => void
 */
export function ExportDialog({ open, canvasW, canvasH, onExport, onClose }) {
  const [format, setFormat] = useState("png");
  const [quality, setQuality] = useState(92);
  const [scale, setScale] = useState(100);
  const [filename, setFilename] = useState("pixalloy");

  useEffect(() => { if (open) setScale(100); }, [open]);

  const fmt = FORMATS.find(f => f.id === format) || FORMATS[0];
  const outW = Math.round(canvasW * scale / 100);
  const outH = Math.round(canvasH * scale / 100);

  const handleExport = useCallback(() => {
    onExport({
      format: fmt.id, mime: fmt.mime,
      quality: fmt.hasQuality ? quality / 100 : 1,
      scale: scale / 100,
      filename: (filename || "pixalloy") + "." + fmt.id,
    });
    onClose();
  }, [fmt, quality, scale, filename, onExport, onClose]);

  if (!open) return null;

  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:12 }}>
        Export Image
      </div>

      {/* Format selector */}
      <div style={{ fontSize:10, color:V.muted, marginBottom:4 }}>Format</div>
      <div style={{ display:"flex", gap:4, marginBottom:12 }}>
        {FORMATS.map(f => (
          <div key={f.id} onClick={() => setFormat(f.id)}
            style={{ padding:"5px 14px", fontSize:11, borderRadius:4, cursor:"pointer", fontFamily:V.mono,
              background:   format===f.id ? "rgba(167,139,250,0.15)" : V.panelActive,
              border:      `1px solid ${format===f.id ? "#a78bfa" : V.panelBorder2}`,
              color:        format===f.id ? "#a78bfa" : V.muted }}>
            {f.label}
          </div>
        ))}
      </div>

      {/* Quality slider (JPEG/WebP only) */}
      {fmt.hasQuality && (
        <AdjRow label="Quality" value={quality} min={10} max={100}
          onChange={v => setQuality(v)} fmt={v => v + "%"}/>
      )}

      {/* Scale */}
      <AdjRow label="Scale" value={scale} min={25} max={400}
        onChange={v => setScale(v)} fmt={v => v + "%"}/>

      {/* Output size readout */}
      <div style={{ fontSize:10, color:V.ghost, marginBottom:8 }}>
        Output: {outW} × {outH} px
        {scale !== 100 && <span style={{ color:V.muted }}> (original: {canvasW} × {canvasH})</span>}
      </div>

      {/* Filename */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>
        <span style={{ fontSize:10, color:V.muted }}>Filename:</span>
        <input type="text" value={filename}
          onChange={e => setFilename(e.target.value)}
          style={{ flex:1, background:V.panelActive, border:`1px solid ${V.panelBorder2}`,
            borderRadius:4, padding:"4px 8px", fontSize:11, color:V.text, outline:"none" }}/>
        <span style={{ fontSize:10, color:V.ghost }}>.{fmt.id}</span>
      </div>

      <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
        <OptBtn onClick={onClose}>Cancel</OptBtn>
        <OptBtn accent onClick={handleExport}>Export</OptBtn>
      </div>
    </Modal>
  );
}
